/**
 * ParteDelDia
 * -----------
 * Modo "Parte del día" del menú Quirófano: se elige una FECHA y un QUIRÓFANO
 * (sesión de ese día) y se ve el parte con todos sus pacientes en orden. En
 * cada paciente se despliegan sus documentos para IMPRIMIR sin entrar a la
 * ficha: consentimientos, indicaciones postoperatorias y recetas.
 *
 * Reutiliza los endpoints ya existentes (no requiere backend nuevo):
 *  - Consentimientos: GET /api/dashboard/patient/{id} → consents[]
 *  - Postoperatorios: GET /api/postoperatorios/documents?patient_id={id}
 *  - Recetas:         GET /api/recetas?patient_id={id}
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { normalizaTexto } from '../../lib/utils';
import {
  Printer, ChevronDown, ChevronRight, ChevronLeft, Loader2, FilePen, BedDouble, Pill,
  Stethoscope, MapPin, CalendarRange, Syringe, CalendarPlus, Clock, Shirt, LogOut,
  Tablet, Eye, CheckCircle2, PackageCheck, XCircle, HeartPulse, UserCheck,
} from 'lucide-react';
import { api, formatDate, mensajeError } from '../../lib/api';
import {
  listCitas, listConceptos, addDaysStr, setCirugiaTiempos, setEstadoCita, esChalazionDeQuirofano,
  esConsultaEnQuirofano, esConceptoRetoque, avisosDelEpisodio, estadosDeAgenda, ESTADOS_CITA,
} from '../../lib/citasApi';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  consentPreviewArgs, recipePreviewArgs, postopPreviewArgs,
  postopRecipesBundlePreviewArgs, truncate,
} from '../../lib/patientDocsHelpers';
import PruebasPreopCard from '../patient/PruebasPreopCard';
import CitaDuplicadaAviso from '../citas/CitaDuplicadaAviso';
import EtiquetasMarcasPaciente from '../citas/EtiquetasMarcasPaciente';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
import PostopQuickDialog from './PostopQuickDialog';
import AnesthesiaSheetDialog from './AnesthesiaSheetDialog';
import { ciDeAnestesiaQueNoSePide } from '../../lib/anestesiaEnVivo';
import DarCitaDialog from '../citas/DarCitaDialog';
import MoveCitaDialog from '../citas/MoveCitaDialog';

// Recetas de un clic del kit post-cirugía. Se busca el medicamento en el
// catálogo de favoritos (por nombre); si no estuviera, se usa el fallback
// con los valores del kit estándar de la clínica.
const QUICK_RECIPES = [
  {
    key: 'augmentine', label: 'Augmentine 500 comp.', match: 'augmentine',
    fallback: {
      medication_name: 'Augmentine 500/125 mg', presentation: 'Comprimidos 500/125 mg',
      dose: 'Amoxicilina 500 mg + Ácido clavulánico 125 mg', route: 'Oral',
      posology: '1 comprimido cada 8 horas', duration: '5 días', packages: 1,
    },
  },
  {
    key: 'terracortril', label: 'Terracortril pomada oft.', match: 'terracortril',
    fallback: {
      medication_name: 'Terracortril oftálmico', presentation: 'Pomada oftálmica 3,5 g',
      dose: 'Oxitetraciclina + Polimixina B + Hidrocortisona', route: 'Oftálmica',
      posology: '1 aplicación 3 veces al día', duration: '7 días', packages: 1,
    },
  },
  {
    key: 'tobradex', label: 'Tobradex colirio', match: 'tobradex',
    fallback: {
      medication_name: 'Tobradex colirio', presentation: 'Colirio en suspensión 5 ml',
      dose: 'Tobramicina 3 mg/ml + Dexametasona 1 mg/ml', route: 'Oftálmica',
      posology: '1 gota 3 veces al día', duration: '7 días', packages: 1,
    },
  },
  {
    key: 'fortecortin', label: 'Fortecortin 4 mg comp.', match: 'fortecortin',
    fallback: {
      medication_name: 'Fortecortin 4 mg', presentation: 'Comprimidos 4 mg',
      dose: 'Dexametasona 4 mg', route: 'Oral',
      posology: '1 comprimido al día', duration: '4 días', packages: 1,
    },
  },
  {
    key: 'moxifloxacino', label: 'Moxifloxacino 400 comp.', match: 'moxifloxacino',
    fallback: {
      medication_name: 'Moxifloxacino 400 mg', presentation: 'Comprimidos 400 mg',
      dose: 'Moxifloxacino 400 mg', route: 'Oral',
      posology: '1 comprimido al día', duration: '5 días', packages: 1,
    },
  },
];

// Cómo se pinta la línea de alergias de la ficha bajo cada paciente: rojo SOLO
// cuando hay algo, ámbar si nadie preguntó, gris si no hay. El texto lo decide
// el servidor (`solo_alergias_de`, «gana el sí») y el color TAMBIÉN
// (`alergias_estado`, `_estado_alergias` en routers/citas_router): «Sin
// alergias conocidas salvo LATEX» empieza por «sin alergias» y por el prefijo
// salía en gris; la regla palabra a palabra del servidor lo pone en rojo, y
// una comprobación en pantalla no es una regla (segunda pasada, 22/9/2026).
// Aquí no se reinterpreta nada: se pone el color que viene y la etiqueta
// delante. Si el estado no viene (servidor viejo), se clasifica por el
// prefijo, como la columna «Alergias» del PDF (`_clase_alergias`).
const CLASE_ALERGIAS = {
  si: 'text-red-700 font-semibold',
  nopreg: 'text-amber-700 font-medium',
  no: 'text-muted-foreground',
};

function describirAlergias(texto, estado) {
  const t = (texto || '').trim();
  if (!t) return null; // el servidor no ha dicho nada: no se inventa
  const bajo = t.toLowerCase();
  let st = estado;
  if (!CLASE_ALERGIAS[st]) {
    if (bajo.startsWith('sin alergias')) st = 'no';
    else if (bajo.startsWith('sin preguntar')) st = 'nopreg';
    else st = 'si';
  }
  if (st === 'no') return { estado: st, texto: t };
  if (st === 'nopreg') return { estado: st, texto: 'Alergias: sin preguntar' };
  // «REVISAR: LATEX» ya lleva su aviso delante; lo demás va etiquetado.
  return { estado: st, texto: bajo.startsWith('revisar:') ? t : `Alergias: ${t}` };
}

// La CHAPA del tipo de cita junto al procedimiento (clínica, 30/9/2026): el
// retoque y el chalazión citado en quirófano la llevan siempre, también
// cuando la cita tiene procedimiento, igual que el parte IMPRESO
// (`rotulo_tipo_qx`, services/agenda_dia_pdf_generator.py). Antes el tipo
// solo se veía si la cita no tenía procedimiento, y pantalla y papel decían
// cosas distintas.
//   · Retoque: la marca `es_retoque` del catálogo de tipos de cita
//     (`esConceptoRetoque`, como el resto de la pantalla), nunca el código.
//   · Chalazión de quirófano: lo dice el servidor en la cita
//     (`chalazion_de_quirofano`, que ya excluye el retoque).
//   · Consulta citada en quirófano (una revisión, una consulta previa;
//     2/10/2026): también lo dice el servidor (`consulta_en_quirofano`). Con
//     su chapa no parece una cirugía más del parte.
// El texto es el nombre del tipo guardado en la cita; si no lo trae, el del
// catálogo, y en último caso la palabra de la marca: lo mismo que el papel.
const slugConcepto = (code) => String(code || '').trim().replace(/\s+/g, '-').toLowerCase();

function rotuloTipoQx(cita, conceptos) {
  if (!cita) return '';
  const retoque = esConceptoRetoque(cita.concept_code, conceptos);
  const consulta = !retoque && esConsultaEnQuirofano(cita);
  if (!retoque && !consulta && !esChalazionDeQuirofano(cita)) return '';
  const slug = slugConcepto(cita.concept_code);
  const delCatalogo = (conceptos || []).find((c) => slugConcepto(c?.code) === slug)?.name || '';
  const marca = retoque ? 'Retoque' : (consulta ? 'Consulta' : 'Chalazión');
  return String(cita.concept_name || '').trim() || delCatalogo || marca;
}

// Para no escribir dos veces lo mismo («Retoque» en la chapa y «Retoque» de
// procedimiento, cuando la cita no tenía otro): sin mayúsculas, tildes ni
// espacios de más, como el papel.
const comparable = (s) => normalizaTexto(s).replace(/\s+/g, ' ').trim();

function PostopRecipesBundle({ postop, recipes, patientName, setPreview }) {
  const [postopId, setPostopId] = useState('');
  const [selectedRecipeIds, setSelectedRecipeIds] = useState(() => new Set());

  useEffect(() => {
    setPostopId((current) => (
      postop.some((doc) => doc.id === current) ? current : (postop[0]?.id || '')
    ));
  }, [postop]);

  useEffect(() => {
    setSelectedRecipeIds(new Set(recipes.map((recipe) => recipe.id)));
  }, [recipes]);

  const setRecipeChecked = (recipeId, checked) => {
    setSelectedRecipeIds((current) => {
      const next = new Set(current);
      if (checked) next.add(recipeId);
      else next.delete(recipeId);
      return next;
    });
  };

  const selectedCount = selectedRecipeIds.size;
  const imprimir = () => {
    if (!postopId) return;
    const orderedIds = recipes
      .filter((recipe) => selectedRecipeIds.has(recipe.id))
      .map((recipe) => recipe.id);
    setPreview(postopRecipesBundlePreviewArgs({
      postopDocId: postopId,
      recipeIds: orderedIds,
      patientName,
    }));
  };

  return (
    <section
      className="border-y border-[#CFE0DA] bg-[#F7FAF9] px-3 py-3"
      data-testid="parte-postop-recetas-bloque"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <PackageCheck className="w-4 h-4 text-[#2E6E5E] shrink-0" />
          <h4 className="text-sm font-semibold text-[#13231E]">Postoperatorio + recetas</h4>
        </div>
        {recipes.length > 0 && (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs text-[#2E6E5E]"
              onClick={() => setSelectedRecipeIds(new Set(recipes.map((recipe) => recipe.id)))}
              data-testid="parte-bloque-todas"
            >
              Todas
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs text-[#2E6E5E]"
              onClick={() => setSelectedRecipeIds(new Set())}
              data-testid="parte-bloque-ninguna"
            >
              Ninguna
            </Button>
          </div>
        )}
      </div>

      {postop.length > 1 && (
        <div className="mb-2.5">
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Postoperatorio
          </label>
          <Select value={postopId} onValueChange={setPostopId}>
            <SelectTrigger className="h-8 text-xs" data-testid="parte-bloque-postop">
              <SelectValue placeholder="Elegir postoperatorio" />
            </SelectTrigger>
            <SelectContent>
              {postop.map((doc) => (
                <SelectItem key={doc.id} value={doc.id}>
                  {doc.template_name || doc.surgery_name || 'Postoperatorio'}
                  {doc.surgery_date ? ` · ${formatDate(doc.surgery_date)}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {recipes.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mb-3">
          {recipes.map((recipe) => {
            const checkboxId = `bundle-rx-${recipe.id}`;
            return (
              <label
                key={recipe.id}
                htmlFor={checkboxId}
                className="flex items-start gap-2 min-w-0 cursor-pointer text-xs text-[#13231E]"
              >
                <Checkbox
                  id={checkboxId}
                  checked={selectedRecipeIds.has(recipe.id)}
                  onCheckedChange={(checked) => setRecipeChecked(recipe.id, checked === true)}
                  data-testid={`parte-bloque-receta-${recipe.id}`}
                />
                <span className="min-w-0">
                  <span className="block break-words">{recipe.medication_name}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {recipe.status === 'printed' ? 'Impresa' : 'Borrador'}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground mb-3">Sin recetas seleccionables.</p>
      )}

      <Button
        type="button"
        size="sm"
        className="h-8 bg-[#2E6E5E] hover:bg-[#1E4A3F]"
        onClick={imprimir}
        disabled={!postopId}
        title="Las recetas en borrador reciben su número oficial al generar el bloque"
        data-testid="parte-bloque-imprimir"
      >
        <Printer className="w-3.5 h-3.5 mr-1.5" />
        {selectedCount
          ? `Imprimir bloque (${selectedCount} receta${selectedCount === 1 ? '' : 's'})`
          : 'Imprimir solo postoperatorio'}
      </Button>
    </section>
  );
}

/** Un paciente del parte: cabecera con nombre/procedimiento, botones de
 *  acción (dar postoperatorio + recetas del kit) y, al desplegar, sus
 *  documentos ya existentes para reimprimir (lazy-load la primera vez). */
function PacienteParte({ index, item, meds, surgeryDate, consentTemplates, conceptos = [], setPreview, esExterna = false, onCitaChanged }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [docs, setDocs] = useState(null); // { consents, postop, recipes }
  const [postopOpen, setPostopOpen] = useState(false);
  const [anestOpen, setAnestOpen] = useState(false);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [sendingConsentId, setSendingConsentId] = useState(null);
  const [queuedConsentIds, setQueuedConsentIds] = useState(() => new Set());
  const [ensuringConsents, setEnsuringConsents] = useState(false);
  const [addingConsent, setAddingConsent] = useState(false);
  // ¿Lleva anestesia la intervención de este día? (D3, 23/9/2026.) Se le
  // pregunta al servidor, que lo decide en vivo con la MISMA regla que la
  // tablet: `null` mientras no se sepa (y entonces el CI se manda, como
  // siempre), `false` para un chalazión con anestesia local.
  const [llevaAnestesia, setLlevaAnestesia] = useState(null);
  // …y si no la lleva porque el registro quirúrgico dice «¿Con sedación?» No
  // (8/10/2026): cambia por dónde se sale si en realidad la hubo.
  const [sinSedacionDelDia, setSinSedacionDelDia] = useState(false);
  // Selección múltiple (2026-08-27, dirección: también desde Quirófano, no solo
  // desde la ficha): consents sin firmar → tablet, recetas → un solo PDF, y
  // varias plantillas de consentimiento a generar de golpe.
  const [selConsents, setSelConsents] = useState(() => new Set());
  const [selRecipes, setSelRecipes] = useState(() => new Set());
  const [adjuntarOpen, setAdjuntarOpen] = useState(false);
  const [selTemplates, setSelTemplates] = useState(() => new Set());
  const alternarEn = (setter) => (id) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const alternarConsent = alternarEn(setSelConsents);
  const alternarRecipe = alternarEn(setSelRecipes);
  const alternarTemplate = alternarEn(setSelTemplates);
  // Flujo del paciente en quirófano, guardado como estado de la cita (se ve
  // también en la agenda): postop_ok = listo para cambiarse · realizada = en
  // recuperación · se_fue = ya ha abandonado la clínica.
  // El chalazión citado en quirófano (dirección, 30/9/2026) lleva en su lugar
  // listo para cambiarse · realizada = «Realizado» · no_realizada = «No
  // realizado». Lo dice el servidor en la cita (`chalazion_de_quirofano`), no
  // el código del concepto.
  // Una CONSULTA citada en quirófano (una revisión, una consulta previa: la
  // clínica, 2/10/2026) lleva los de consulta —sala de espera, enfermería y
  // visto por enfermería (15/10/2026), consulta, realizada— y nada de lo de
  // una cirugía: ni postoperatorio, ni hoja de
  // anestesia, ni recetas del kit, ni CI que generar. Sigue en el parte porque
  // está en el quirófano. También lo dice el servidor (`consulta_en_quirofano`).
  const [estadoQx, setEstadoQx] = useState(item.cita?.estado_paciente || null);
  const [marcandoListo, setMarcandoListo] = useState(false);
  const chalazionQx = esChalazionDeQuirofano(item.cita);
  const consultaQx = esConsultaEnQuirofano(item.cita);
  const estadosConsulta = consultaQx ? estadosDeAgenda({ tipo: 'quirofano' }, item.cita) : [];
  const rotuloTipo = rotuloTipoQx(item.cita, conceptos);
  const procTexto = rotuloTipo && comparable(item.procedure_text) === comparable(rotuloTipo)
    ? '' : (item.procedure_text || '');
  const postopExplicado = estadoQx === 'postop_ok';
  const enRecuperacion = estadoQx === 'realizada';
  const seFue = estadoQx === 'se_fue';
  const noRealizado = estadoQx === 'no_realizada';
  // Un estado guardado que ya no tiene botón en esta cita —un chalazión
  // marcado «Se fue» antes del cambio, o una cita que dejó de ser chalazión
  // con «No realizado»— se enseña aparte, encendido, y un clic lo quita.
  const botonesQx = consultaQx
    ? estadosConsulta.map((e) => e.value)
    : chalazionQx
      ? ['postop_ok', 'realizada', 'no_realizada']
      : ['postop_ok', 'realizada', 'se_fue'];
  const estadoAnterior = estadoQx && !botonesQx.includes(estadoQx)
    ? (ESTADOS_CITA.find((e) => e.value === estadoQx)?.label || estadoQx)
    : null;

  const marcarEstadoQx = async (valor, mensaje) => {
    if (!item.cita?.id) return;
    const siguiente = estadoQx === valor ? null : valor;
    setMarcandoListo(true);
    try {
      const actualizada = await setEstadoCita(item.cita.id, siguiente);
      setEstadoQx(siguiente);
      toast.success(siguiente ? `${item.patient_name || 'Paciente'}: ${mensaje}` : 'Marca retirada');
      // Cirugía o retoque en recuperación / se fue: el backend deja un
      // episodio en la ficha del paciente (2026-09-08, punto 4). El chalazión,
      // con «Realizado» o «No realizado» (30/9/2026).
      avisosDelEpisodio(actualizada, item.cita).forEach((a) => toast[a.tipo](a.texto));
      onCitaChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo marcar');
    } finally {
      setMarcandoListo(false);
    }
  };
  const marcarListoCambiarse = () => marcarEstadoQx('postop_ok', 'listo para cambiarse');
  const marcarSeFue = () => marcarEstadoQx('se_fue', 'se fue de la clínica');
  const [rxCreating, setRxCreating] = useState(null); // key de QUICK_RECIPES en curso
  // Tiempos reales de cirugía (quirófano de externos → facturar).
  const [cInicio, setCInicio] = useState(item.cirugia_inicio || '');
  const [cFin, setCFin] = useState(item.cirugia_fin || '');
  const [savingTiempos, setSavingTiempos] = useState(false);
  const patientName = item.patient_name || 'Paciente';
  const alergias = describirAlergias(item.alergias, item.alergias_estado);

  const cirDur = useMemo(() => {
    if (!cInicio || !cFin) return null;
    const [h1, m1] = cInicio.split(':').map(Number);
    const [h2, m2] = cFin.split(':').map(Number);
    if ([h1, m1, h2, m2].some((n) => Number.isNaN(n))) return null;
    let d = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (d < 0) d += 24 * 60;
    return d;
  }, [cInicio, cFin]);

  const guardarTiempos = async (inicio, fin) => {
    if (!item.id) return;
    setSavingTiempos(true);
    try {
      await setCirugiaTiempos(item.id, { inicio, fin });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar el tiempo de cirugía');
    } finally {
      setSavingTiempos(false);
    }
  };

  // Crear una receta del kit y abrir su PDF (al imprimir consume el CVE).
  const crearReceta = async (qr) => {
    if (!item.patient_id || rxCreating) return;
    setRxCreating(qr.key);
    try {
      const med = (meds || []).find(
        (m) => normalizaTexto(m.name).includes(qr.match)
      );
      // `meds` en null = el catálogo no se ha podido leer (22/9/2026). La
      // receta sale igual con la pauta del kit, que es para lo que está,
      // pero se dice: no es lo mismo que el favorito no exista que no saber
      // cuál es, y la pauta puede no ser la que la clínica tiene guardada.
      if (!med && meds === null) {
        toast.warning('No se ha podido leer el catálogo de medicamentos: la receta lleva '
          + 'la pauta del kit estándar. Revísala antes de imprimirla.', { duration: 7000 });
      }
      const base = med
        ? {
            medication_id: med.id, medication_name: med.name,
            presentation: med.presentation, dose: med.dose, route: med.route,
            posology: med.posology || '', duration: med.duration || '',
            packages: med.packages || 1,
          }
        : { ...qr.fallback };
      const res = await api.post('/api/recetas', { patient_id: item.patient_id, ...base });
      // Aviso si la ficha no tiene DNI/fecha de nacimiento (la receta sale
      // incompleta hasta completarlos en el paciente).
      const falta = res.data?.ficha_incompleta;
      if (falta?.length) {
        toast.warning(`${patientName}: falta ${falta.join(' y ')} en su ficha — la receta saldrá incompleta. Complétalo en Pacientes.`, { duration: 7000 });
      }
      setDocs(null); // que al desplegar se recarguen los documentos
      setPreview(recipePreviewArgs({
        recipeId: res.data.id, medicationName: res.data.medication_name, patientName,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la receta');
    } finally {
      setRxCreating(null);
    }
  };

  // Postop creado desde el diálogo → abrir su PDF para imprimir.
  //
  // OJO con el respiro antes de abrir la vista previa: se estaban cerrando y
  // abriendo DOS diálogos en el mismo instante, y a veces el segundo salía en
  // blanco (Radix deja el primero desmontándose y el nuevo se monta encima).
  // Es el único sitio de la app que encadena dos diálogos así, y es justo el
  // que fallaba «en muchas ocasiones» (2026-08-05). Dejando que el primero
  // termine de cerrarse, el visor abre siempre.
  const onPostopCreado = (doc) => {
    setDocs(null);
    setTimeout(() => {
      setPreview(postopPreviewArgs({
        docId: doc.id, templateName: doc.template_name, patientName,
      }));
    }, 250);
  };

  // VACÍO Y «NO SE HA PODIDO LEER» NO SON LO MISMO (21/9/2026).
  // Los tres GET se pedían con un `.catch(() => [])` cada uno: si el servidor
  // contestaba 500, la lista quedaba vacía y esta pantalla —la que se mira
  // JUSTO ANTES de entrar a quirófano— le decía a enfermería «Consentimientos
  // (0)» y «Sin otros documentos para este paciente». El toast del catch de
  // fuera no llegaba a dispararse nunca, porque los tres fallos ya estaban
  // tragados. O se opera creyendo que falta el CI, o se «reparan» con el
  // ensure unos consentimientos que no estaban rotos: duplicados.
  //
  // El motivo se pinta después como texto, así que tiene que SER texto: el
  // `detail` de un 422 de FastAPI es una lista de objetos y React tira el
  // parte entero al pintarla (`mensajeError` lo resuelve; segunda pasada,
  // 22/9/2026).
  //
  // Y un 200 que NO es una lista tampoco es «no hay» (22/9/2026, lo que dejó
  // el supervisor): el fallback del SPA contesta su index.html con un 200, y
  // convertirlo en `[]` volvía a decir «Consentimientos (0)» sin aviso y a
  // lanzar el ensure. Es un fallo de lectura, con la misma frase que el
  // listado del quirófano (más abajo, `listCitas`) y `leerLista`.
  const pedirBloque = (ruta) => api
    .get(ruta, { params: { patient_id: item.patient_id } })
    .then((r) => (Array.isArray(r.data)
      ? { items: r.data }
      : { items: [], fallo: 'el servidor ha contestado algo que no es una lista' }))
    .catch((e) => ({ items: [], fallo: mensajeError(e, 'no se ha podido consultar') }));

  const cargar = async () => {
    if (docs || !item.patient_id) return;
    setLoading(true);
    try {
      const [consents, po, rx] = await Promise.all([
        pedirBloque('/api/consents'),
        pedirBloque('/api/postoperatorios/documents'),
        pedirBloque('/api/recetas'),
      ]);

      // Repara citas de quirófano antiguas que se crearon antes de la
      // auto-generación de consentimientos. El endpoint es idempotente.
      // NO se lanza cuando la lista viene de un fallo: «no hay ninguno» y «no
      // se sabe» no autorizan lo mismo.
      let deConsents = consents.items;
      // Una consulta citada en quirófano no genera CI (2/10/2026).
      if (!consents.fallo && !deConsents.length && item.cita?.id && item.procedure_text
          && !consultaQx) {
        const ensured = await api.post(`/api/citas/${item.cita.id}/consents/ensure`)
          .then((r) => r.data)
          .catch(() => null);
        if (ensured?.consents) deConsents = ensured.consents;
      }
      setDocs({
        consents: deConsents,
        postop: po.items,
        recipes: rx.items,
        fallos: { consents: consents.fallo, postop: po.fallo, recipes: rx.fallo },
      });
      const rotos = [consents.fallo && 'consentimientos', po.fallo && 'postoperatorios',
        rx.fallo && 'recetas'].filter(Boolean);
      if (rotos.length) {
        toast.error(`${patientName}: no se han podido cargar ${rotos.join(', ')}. `
          + 'Lo que ves debajo no es lo que tiene el paciente.');
      }

      // Lo que ya está en el iPad se lo pregunta al servidor, que es quien lo
      // sabe: antes solo se marcaba lo que se hubiera pulsado en esta misma
      // sesión y una recarga de la página lo perdía. La cola es de gestión
      // (403 para enfermería): en ese caso no se marca nada, que es el lado
      // seguro —el botón dice «Tablet», no «En tablet»—.
      api.get('/api/consent-tablet/queue', { params: { patient_id: item.patient_id } })
        .then((r) => {
          const enCola = (r.data?.items || [])
            .filter((p) => p.status === 'pending' && p.consent_id
              && (p.en_tablet ?? (!!p.tablet || p.via !== 'link')))
            .map((p) => p.consent_id);
          if (enCola.length) setQueuedConsentIds(new Set(enCola));
        })
        .catch(() => {});
    } catch (e) {
      // Sin esto la pantalla se queda sin `docs` y el efecto de arriba la
      // pondría a pedirlos otra vez sin parar. Y, sobre todo: se dice.
      const motivo = mensajeError(e, 'error inesperado');
      setDocs({ consents: [], postop: [], recipes: [],
        fallos: { consents: motivo, postop: motivo, recipes: motivo } });
      toast.error('No se pudieron cargar los documentos del paciente');
    } finally {
      setLoading(false);
    }
  };

  /**
   * ¿Lleva anestesia la intervención de este día? (D3, 23/9/2026.) Se le
   * pregunta al servidor, que lo decide en vivo con la MISMA regla que la
   * tablet, y se vuelve a preguntar cada vez que se despliega la fila y al
   * cerrar «Firmas»: ahí se elige el anestesista o se firma la hoja, y la marca
   * «Anestesia local» puede dejar de valer. Si no se puede preguntar, se queda
   * en «no se sabe» y el CI se manda como siempre (la tablet decide).
   */
  const preguntarLlevaAnestesia = () => {
    if (!surgeryDate || !item.patient_id) return;
    api.get('/api/anesthesia-records/lleva-anestesia',
      { params: { patient_id: item.patient_id, surgery_date: surgeryDate } })
      .then((r) => {
        setLlevaAnestesia(typeof r.data?.lleva_anestesia === 'boolean' ? r.data.lleva_anestesia : null);
        setSinSedacionDelDia(r.data?.lleva_anestesia === false && r.data?.anestesia?.sin_sedacion === true);
      })
      .catch(() => { setLlevaAnestesia(null); setSinSedacionDelDia(false); });
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      cargar();
      preguntarLlevaAnestesia();
    }
  };

  // Crear una receta o un postop desde aquí hace `setDocs(null)` para que los
  // documentos se vuelvan a leer... pero el panel se queda ABIERTO, que es lo
  // normal (se crean desde él). La pantalla seguía pintando con `docs` en
  // null y reventaba entera: el parte del día completo en blanco, en
  // quirófano (medido el 21/9/2026 al pulsar una receta del kit con el
  // paciente desplegado). Si se quedan sin leer y el panel sigue abierto, se
  // vuelven a pedir.
  useEffect(() => {
    if (open && !docs && !loading) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, docs, loading]);

  const total = docs ? docs.consents.length + docs.postop.length + docs.recipes.length : null;
  // Si algún bloque no se ha podido leer, «no hay documentos» no se puede
  // decir: no se sabe. La pantalla lo dice en rojo y se calla el resto.
  const falloConsents = docs?.fallos?.consents;
  const algoNoSeLeyo = !!(falloConsents || docs?.fallos?.postop || docs?.fallos?.recipes);
  // Los que se pueden mandar a la tablet. Sin el CI de anestesia de una
  // intervención que no lleva anestesia (23/9/2026, DECISIÓN D3 de dirección: el
  // chalazión con anestesia local): no se le pide a la paciente y la tablet lo
  // rechaza, así que «Enviar pendientes a tablet» salía siempre con un fallo.
  const citaDelParte = item.cita?.id;
  const noSePide = (c) => ciDeAnestesiaQueNoSePide(c, llevaAnestesia,
    { fecha: surgeryDate, citaId: citaDelParte });
  const unsignedConsents = useMemo(
    () => (docs?.consents || []).filter((c) => !c.patient_signature_b64
      && !ciDeAnestesiaQueNoSePide(c, llevaAnestesia, { fecha: surgeryDate, citaId: citaDelParte })),
    [docs, llevaAnestesia, surgeryDate, citaDelParte],
  );

  const markConsentQueued = (consentId) => {
    setQueuedConsentIds((prev) => {
      const next = new Set(prev);
      next.add(consentId);
      return next;
    });
  };

  const enviarConsentimientoTablet = async (consent, { silent = false } = {}) => {
    if (!consent?.id || sendingConsentId) return false;
    setSendingConsentId(consent.id);
    try {
      const r = await api.post('/api/consent-tablet/send', { consent_id: consent.id });
      markConsentQueued(consent.id);
      if (!silent) {
        if (r.data?.reused) {
          toast.info(`${consent.template_name}: ya estaba en la tablet`);
        } else {
          toast.success(`${consent.template_name}: enviado a la tablet`);
        }
      }
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar a la tablet');
      return false;
    } finally {
      setSendingConsentId(null);
    }
  };

  const enviarLoteTablet = async (ids) => {
    if (!ids.length || sendingConsentId) return;
    setSendingConsentId('__lote__');
    try {
      const r = await api.post('/api/consent-tablet/send', { consent_ids: ids });
      // Se marcan en verde SOLO los que el servidor dice que ha encolado
      // (2026-09-19). Desde que el envío en tanda devuelve 200 con la lista de
      // `fallidos` en vez de reventar entera, marcar `ids` a pelo pintaba «En
      // tablet» sobre un consentimiento que el servidor había RECHAZADO — y
      // esta es la pantalla que se mira justo antes de entrar a quirófano: se
      // pasa el paciente creyendo que tiene el consentimiento en el iPad.
      const resultados = r.data?.resultados || [];
      const fallidos = r.data?.fallidos || [];
      const encolados = resultados.length
        ? resultados.filter((x) => x.ok).map((x) => x.consent_id)
        : ids.filter((id) => !fallidos.some((f) => f.consent_id === id));
      encolados.forEach(markConsentQueued);
      const n = (r.data?.enviados ?? 0) + (r.data?.reutilizados ?? 0);
      if (fallidos.length) {
        toast.warning(`${n} de ${ids.length} en la cola · falló ${fallidos.length}: `
          + fallidos.map((f) => `${f.nombre} (${f.motivo})`).join(' · '));
      } else {
        toast.success(`${n} consentimiento${n === 1 ? '' : 's'} en la cola de la tablet`);
      }
      setSelConsents(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron enviar a la tablet');
    } finally {
      setSendingConsentId(null);
    }
  };

  const enviarTodosPendientesTablet = () =>
    enviarLoteTablet(unsignedConsents.map((c) => c.id));

  const generarConsentimientos = async () => {
    if (!item.cita?.id || ensuringConsents) return;
    setEnsuringConsents(true);
    try {
      const r = await api.post(`/api/citas/${item.cita.id}/consents/ensure`);
      const created = r.data?.created || [];
      setDocs((prev) => ({ ...(prev || {}), consents: r.data?.consents || [] }));
      if (created.length) {
        toast.success(`${created.length} consentimiento${created.length === 1 ? '' : 's'} generado${created.length === 1 ? '' : 's'}`);
      } else {
        toast.info('No hay un consentimiento asociado a este procedimiento');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron generar los consentimientos');
    } finally {
      setEnsuringConsents(false);
    }
  };

  // Algunas citas históricas de quirófano solo tienen el tipo "Cirugía" y
  // no el procedimiento detallado. En ese caso no es seguro adivinar la
  // plantilla: el personal elige aquí el consentimiento correcto.
  const adjuntarSeleccion = async () => {
    const codes = Array.from(selTemplates);
    if (!codes.length || addingConsent) return;
    setAddingConsent(true);
    let creados = 0;
    try {
      for (const code of codes) {
        // De qué cita sale: el papel es del CIRUJANO DE ESTA CIRUGÍA, lo
        // adjunte quien lo adjunte. Lo resuelve el servidor (21/9/2026): sin
        // esto, un consentimiento de una cirugía de un cirujano externo salía a
        // nombre de quien lo estuviera preparando.
        const payload = { patient_id: item.patient_id, template_code: code, cita_id: item.id };
        if (surgeryDate) {
          payload.surgery_date = surgeryDate;
          payload.doc_date = addDaysStr(surgeryDate, -2);
        }
        // eslint-disable-next-line no-await-in-loop
        const response = await api.post('/api/consents', payload);
        const created = response.data;
        setDocs((current) => ({
          ...(current || { postop: [], recipes: [] }),
          consents: [created, ...((current || {}).consents || [])],
        }));
        // ADJUNTAR NO ES MANDAR (21/9/2026): aquí se llamaba a
        // `markConsentQueued` sin haber pasado por /api/consent-tablet/send,
        // y el botón del recién creado quedaba en «En tablet». Esta es la
        // pantalla que se mira justo antes de entrar a quirófano: se pasaba
        // al paciente creyendo que tenía el consentimiento en el iPad. Es el
        // mismo fallo que se arregló el 19/9 en `enviarLoteTablet` —marcar
        // solo lo que el servidor confirma—, por la otra puerta.
        creados += 1;
      }
      toast.success(`${creados} consentimiento${creados === 1 ? '' : 's'} adjuntado${creados === 1 ? '' : 's'} y listo${creados === 1 ? '' : 's'} para la tablet`);
      setSelTemplates(new Set());
      setAdjuntarOpen(false);
    } catch (e) {
      toast.error(creados
        ? `Se adjuntaron ${creados} de ${codes.length}: ${e?.response?.data?.detail || 'error'}`
        : (e?.response?.data?.detail || 'No se pudo adjuntar el consentimiento'));
    } finally {
      setAddingConsent(false);
    }
  };

  const adjuntarConsentimiento = async (templateCode) => {
    if (!templateCode || templateCode === '__none__' || addingConsent) return;
    setAddingConsent(true);
    try {
      const payload = {
        patient_id: item.patient_id,
        template_code: templateCode,
        cita_id: item.id,        // a nombre del cirujano de esta cirugía
      };
      if (surgeryDate) {
        payload.surgery_date = surgeryDate;
        payload.doc_date = addDaysStr(surgeryDate, -2);
      }
      const response = await api.post('/api/consents', payload);
      const created = response.data;
      setDocs((current) => ({
        ...(current || { postop: [], recipes: [] }),
        consents: [created, ...((current || {}).consents || [])],
      }));
      // Adjuntar no es mandar: el «En tablet» lo pone el envío (ver arriba).
      toast.success(created.template_name + ': consentimiento adjuntado y listo para enviar a la tablet');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo adjuntar el consentimiento');
    } finally {
      setAddingConsent(false);
    }
  };

  return (
    <li className="border border-[#D9E3DD] rounded-lg overflow-hidden" data-testid={`parte-paciente-${item.patient_id || index}`}>
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-start gap-2 px-3 py-2.5 text-left hover:bg-[rgba(46,110,94,0.04)] transition-colors"
      >
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />}
        <span className="tabular-nums text-muted-foreground shrink-0 mt-0.5">{index + 1}.</span>
        <span className="min-w-0 flex-1">
          <span className="block">
            <span className="font-semibold text-[#13231E]">{patientName}</span>
            {/* Sus marcas de paciente (administración, 21/10/2026): la
                etiqueta de su color junto al nombre, como en la agenda. */}
            <EtiquetasMarcasPaciente
              marcas={item.cita?.marcas_paciente}
              testId={`parte-marcas-${item.patient_id || index}`}
              className="ml-1.5 align-middle"
            />
            {item.time && <span className="text-muted-foreground tabular-nums"> · {item.time}</span>}
            {rotuloTipo ? (
              <span className="text-muted-foreground">
                {' — '}
                <span
                  className="inline-flex items-center rounded border border-[#284264] px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-wide text-[#284264] leading-none align-middle"
                  data-testid={`parte-tipo-qx-${item.patient_id || index}`}
                >
                  {rotuloTipo}
                </span>
                {procTexto ? ` ${procTexto}` : ''}
              </span>
            ) : (
              procTexto && <span className="text-muted-foreground"> — {procTexto}</span>
            )}
          </span>
          {/* Nota de la cita: procedimiento detallado + alergias */}
          {item.notes && (
            <span className="block text-[13px] text-[#13231E]/90 whitespace-pre-wrap mt-0.5">{item.notes}</span>
          )}
          {/* Las alergias de la FICHA (no de la nota), tal como las manda el
              servidor. Rojo cuando hay algo —también el «REVISAR:» de una
              casilla en «no» con una alergia escrita al lado—, ámbar cuando
              nadie ha preguntado, y nunca en blanco: en blanco no se
              distingue de «ya lo he mirado y no hay nada» (G9, 22/9/2026). */}
          {alergias && (
            <span
              className={`block text-[13px] mt-0.5 ${CLASE_ALERGIAS[alergias.estado]}`}
              data-testid="parte-alergias"
              data-estado={alergias.estado}
            >
              {alergias.texto}
            </span>
          )}
          {/* La misma paciente citada a la vez en otro sitio (23/9/2026): la
              marca la pone el servidor (`duplicada_en`). */}
          <CitaDuplicadaAviso duplicada={item.cita?.duplicada_en} nombre={item.patient_name} testId={`parte-cita-duplicada-${item.id}`} className="mt-1" />
        </span>
        {!item.patient_id && (
          <span className="text-[11px] text-amber-700 shrink-0 mt-0.5">sin ficha</span>
        )}
      </button>

      {/* Acciones rápidas de quirófano: dar postoperatorio + recetas del kit.
          En una consulta citada en quirófano (2/10/2026), las de consulta. */}
      {item.patient_id && (
        <div className="flex flex-wrap items-center gap-1.5 px-3 pb-2.5 pl-9">
          {!consultaQx && (
          <Button
            size="sm"
            className="h-7 text-xs bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            onClick={() => setPostopOpen(true)}
            data-testid={`parte-dar-postop-${item.patient_id}`}
          >
            <BedDouble className="w-3 h-3 mr-1.5" /> Dar postoperatorio
          </Button>
          )}
          {item.cita && consultaQx && estadosConsulta.map((e) => {
            const activo = estadoQx === e.value;
            return (
              <Button
                key={e.value}
                size="sm"
                variant={activo ? 'default' : 'outline'}
                className={`h-7 text-xs ${activo ? 'text-white' : 'text-[#13231E]'}`}
                style={activo
                  ? { backgroundColor: e.color, borderColor: e.color }
                  : { borderColor: e.color }}
                onClick={() => marcarEstadoQx(e.value, e.label.toLowerCase())}
                disabled={marcandoListo}
                data-testid={`parte-estado-${e.value}-${item.patient_id}`}
                title={`Estado de la consulta: ${e.label}`}
              >
                {/* Enfermería y su visto (15/10/2026), con el color de su estado. */}
                {e.value === 'tratamiento'
                  ? <Stethoscope className={`w-3 h-3 mr-1.5 ${activo ? '' : 'text-[#2563EB]'}`} />
                  : e.value === 'realizada'
                    ? <CheckCircle2 className={`w-3 h-3 mr-1.5 ${activo ? '' : 'text-emerald-600'}`} />
                    : e.value === 'enfermeria'
                      ? <HeartPulse className="w-3 h-3 mr-1.5" style={activo ? undefined : { color: e.color }} />
                      : e.value === 'visto_enfermeria'
                        ? <UserCheck className="w-3 h-3 mr-1.5" style={activo ? undefined : { color: e.color }} />
                        : <Clock className={`w-3 h-3 mr-1.5 ${activo ? '' : 'text-[#D97706]'}`} />}
                {activo ? `${e.label} ✓` : e.label}
              </Button>
            );
          })}
          {item.cita && !consultaQx && (
            <Button
              size="sm"
              variant={postopExplicado ? 'default' : 'outline'}
              className={`h-7 text-xs ${postopExplicado
                ? 'bg-[#7C3AED] hover:bg-[#6D28D9] text-white'
                : 'border-[#7C3AED] text-[#13231E]'}`}
              onClick={marcarListoCambiarse}
              disabled={marcandoListo}
              data-testid={`parte-listo-cambiarse-${item.patient_id}`}
              title="El paciente ya tiene el postoperatorio explicado y puede pasar a cambiarse"
            >
              {marcandoListo
                ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                : <Shirt className={`w-3 h-3 mr-1.5 ${postopExplicado ? '' : 'text-[#7C3AED]'}`} />}
              {postopExplicado ? 'Listo para cambiarse ✓' : 'Listo para cambiarse'}
            </Button>
          )}
          {/* Chalazión en quirófano (dirección, 30/9/2026): «Realizado» y «No
              realizado» en lugar de «En recuperación» y «Se fue». Los dos
              dejan un episodio en la ficha, cada uno con lo suyo. */}
          {item.cita && chalazionQx && (
            <Button
              size="sm"
              variant={enRecuperacion ? 'default' : 'outline'}
              className={`h-7 text-xs ${enRecuperacion
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'border-emerald-400 text-[#13231E]'}`}
              onClick={() => marcarEstadoQx('realizada', 'realizado')}
              disabled={marcandoListo}
              data-testid={`parte-realizado-${item.patient_id}`}
              title="Se ha extirpado el chalazión: queda un episodio en la ficha"
            >
              <CheckCircle2 className={`w-3 h-3 mr-1.5 ${enRecuperacion ? '' : 'text-emerald-600'}`} />
              {enRecuperacion ? 'Realizado ✓' : 'Realizado'}
            </Button>
          )}
          {item.cita && chalazionQx && (
            <Button
              size="sm"
              variant={noRealizado ? 'default' : 'outline'}
              className={`h-7 text-xs ${noRealizado
                ? 'bg-rose-600 hover:bg-rose-700 text-white'
                : 'border-rose-400 text-[#13231E]'}`}
              onClick={() => marcarEstadoQx('no_realizada', 'no realizado')}
              disabled={marcandoListo}
              data-testid={`parte-no-realizado-${item.patient_id}`}
              title="No se ha realizado el tratamiento: queda un episodio en la ficha"
            >
              <XCircle className={`w-3 h-3 mr-1.5 ${noRealizado ? '' : 'text-rose-600'}`} />
              {noRealizado ? 'No realizado ✓' : 'No realizado'}
            </Button>
          )}
          {item.cita && estadoAnterior && (
            <Button
              size="sm"
              variant="default"
              className="h-7 text-xs bg-slate-600 hover:bg-slate-700 text-white"
              onClick={() => marcarEstadoQx(estadoQx, '')}
              disabled={marcandoListo}
              data-testid={`parte-estado-anterior-${item.patient_id}`}
              title="Estado marcado antes del cambio de botones: púlsalo para quitarlo"
            >
              {estadoAnterior} ✓
            </Button>
          )}
          {item.cita && !chalazionQx && !consultaQx && (
            <Button
              size="sm"
              variant={enRecuperacion ? 'default' : 'outline'}
              className={`h-7 text-xs ${enRecuperacion
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'border-emerald-400 text-[#13231E]'}`}
              onClick={() => marcarEstadoQx('realizada', 'en recuperación')}
              disabled={marcandoListo}
              data-testid={`parte-en-recuperacion-${item.patient_id}`}
              title="El paciente está en recuperación tras la cirugía"
            >
              <BedDouble className={`w-3 h-3 mr-1.5 ${enRecuperacion ? '' : 'text-emerald-600'}`} />
              {enRecuperacion ? 'En recuperación ✓' : 'En recuperación'}
            </Button>
          )}
          {item.cita && !chalazionQx && !consultaQx && (
            <Button
              size="sm"
              variant={seFue ? 'default' : 'outline'}
              className={`h-7 text-xs ${seFue
                ? 'bg-slate-600 hover:bg-slate-700 text-white'
                : 'border-slate-400 text-[#13231E]'}`}
              onClick={marcarSeFue}
              disabled={marcandoListo}
              data-testid={`parte-se-fue-${item.patient_id}`}
              title="El paciente ya ha abandonado la clínica"
            >
              <LogOut className={`w-3 h-3 mr-1.5 ${seFue ? '' : 'text-slate-500'}`} />
              {seFue ? 'Se fue ✓' : 'Se fue'}
            </Button>
          )}
          {!consultaQx && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs border-[#2E6E5E] text-[#13231E]"
            onClick={() => setAnestOpen(true)}
            data-testid={`parte-hoja-anestesia-${item.patient_id}`}
          >
            <Syringe className="w-3 h-3 mr-1.5 text-[#2E6E5E]" /> Hoja de anestesia
          </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs border-[#2E6E5E] text-[#13231E]"
            onClick={() => setRevisionOpen(true)}
            data-testid={`parte-cita-revision-${item.patient_id}`}
          >
            <CalendarPlus className="w-3 h-3 mr-1.5 text-[#2E6E5E]" /> Dar cita de revisión
          </Button>
          {item.cita && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-[#2E6E5E] text-[#13231E]"
              onClick={() => setMoveOpen(true)}
              data-testid={`parte-cambiar-hora-${item.patient_id}`}
            >
              <Clock className="w-3 h-3 mr-1.5 text-[#2E6E5E]" /> Cambiar hora o duración
            </Button>
          )}
          {!consultaQx && QUICK_RECIPES.map((qr) => (
            <Button
              key={qr.key}
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              disabled={!!rxCreating}
              onClick={() => crearReceta(qr)}
              data-testid={`parte-rx-${qr.key}-${item.patient_id}`}
            >
              {rxCreating === qr.key
                ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                : <Pill className="w-3 h-3 mr-1.5 text-[#2E6E5E]" />}
              {qr.label}
            </Button>
          ))}
        </div>
      )}

      {/* Externos: las horas de CIRUGÍA para facturarles (2026-07-17).
          LOS RÓTULOS, DEL 19/9/2026 (dirección). Estas dos casillas decían
          «Cirugía: Inicio [__] Fin [__]», y desde hoy la MISMA auxiliar que
          las rellena registra además cuándo el paciente ENTRA y SALE por la
          puerta del quirófano. Lo que se escriba aquí es el RESPALDO DE
          FACTURACIÓN: cuando el registro no tiene las horas de las firmas del
          cirujano, el informe de externos las usa tal cual como horas de
          cirugía. Si aquí entra una hora de puerta, se le factura de más al
          cirujano externo —y justo en el caso para el que existe el encargo—.
          Por eso el rótulo es el MISMO que el del registro («Inicio cirugía» /
          «Fin cirugía») y dice de quién son y de quién no. */}
      {esExterna && item.patient_id && !consultaQx && (
        <div
          className="px-3 pb-2.5 pl-9 text-xs"
          data-testid={`parte-cir-tiempos-${item.patient_id}`}
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground inline-flex items-center gap-1">
              <Clock className="w-3 h-3 text-[#2E6E5E]" /> Horas de la cirugía:
            </span>
            <label className="inline-flex items-center gap-1 text-[#13231E]">
              Inicio cirugía
              <input
                type="time"
                value={cInicio}
                onChange={(e) => setCInicio(e.target.value)}
                onBlur={() => guardarTiempos(cInicio, cFin)}
                className="h-7 rounded border border-[#dbe3ee] px-1.5 tabular-nums"
                data-testid={`parte-cir-inicio-${item.patient_id}`}
              />
            </label>
            <label className="inline-flex items-center gap-1 text-[#13231E]">
              Fin cirugía
              <input
                type="time"
                value={cFin}
                onChange={(e) => setCFin(e.target.value)}
                onBlur={() => guardarTiempos(cInicio, cFin)}
                className="h-7 rounded border border-[#dbe3ee] px-1.5 tabular-nums"
                data-testid={`parte-cir-fin-${item.patient_id}`}
              />
            </label>
            {cirDur != null && (
              <span className="font-semibold text-[#13231E]">· {cirDur} min de cirugía</span>
            )}
            {savingTiempos && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Las del cirujano: desde que empieza a operar hasta que termina. NO son las de la
            puerta del quirófano (cuándo entra y sale el paciente): esas se marcan en el registro
            y miden ocupación de sala. Cuando el cirujano no ha firmado en la tablet, el fin que se
            le factura es el de aquí (y el inicio, si no hay hora de entrada por la puerta).
          </p>
        </div>
      )}

      {item.cita && (
        <MoveCitaDialog
          open={moveOpen}
          onOpenChange={setMoveOpen}
          cita={item.cita}
          onMoved={() => { setMoveOpen(false); onCitaChanged?.(); }}
        />
      )}

      <PostopQuickDialog
        open={postopOpen}
        onOpenChange={setPostopOpen}
        patient={item}
        surgeryDate={surgeryDate}
        surgeryName={item.procedure_text || ''}
        /* Qué hoja le toca lo dice el servidor con la cita (22/9/2026, M1).
           Aquí antes se decidía con `concept_code === 'chalazion'`: un
           chalazión citado en quirófano como «Cirugía menor» con la palabra
           en la nota se quedaba sin su hoja, y la blefaro sin la suya. */
        citaId={item.cita?.id}
        onCreated={onPostopCreado}
      />

      {item.patient_id && (
        <AnesthesiaSheetDialog
          open={anestOpen}
          onOpenChange={(o) => {
            setAnestOpen(o);
            if (!o) preguntarLlevaAnestesia();
          }}
          patient={item}
          surgeryDate={surgeryDate}
          agendaId={item.agenda_id}
          citaId={item.id}
        />
      )}

      {item.patient_id && (
        <DarCitaDialog
          open={revisionOpen}
          onOpenChange={setRevisionOpen}
          patient={{ id: item.patient_id, patient_name: patientName }}
          titulo="Dar cita de revisión"
          defaultConceptCode="primera-revision"
        />
      )}

      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-[#EEF3F0] bg-[rgba(46,110,94,0.02)]">
          {loading || (item.patient_id && !docs) ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando documentos…
            </div>
          ) : !item.patient_id ? (
            <p className="text-sm text-muted-foreground py-2">Este paciente no está vinculado a una ficha, no hay documentos que imprimir.</p>
          ) : (
            <div className="space-y-2.5 pt-1">
              {/* Pruebas preoperatorias que mandó el paciente (analítica, ECG):
                  el anestesista las abre aquí, sin papel. */}
              <div>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1">
                  Pruebas preoperatorias del paciente
                </div>
                <PruebasPreopCard
                  patientId={item.patient_id}
                  compact
                  canDelete={false}
                  onPreview={(args) => setPreview(args)}
                />
              </div>
              {total === 0 && !algoNoSeLeyo && (
                <p className="text-sm text-muted-foreground py-2">Sin otros documentos para este paciente.</p>
              )}
              {(docs.fallos?.postop || docs.fallos?.recipes) && (
                <p className="text-sm text-red-700 py-1" data-testid={`parte-docs-fallo-${item.patient_id}`}>
                  No se han podido cargar
                  {docs.fallos?.postop ? ' los postoperatorios' : ''}
                  {docs.fallos?.postop && docs.fallos?.recipes ? ' ni' : ''}
                  {docs.fallos?.recipes ? ' las recetas' : ''}
                  {' '}de este paciente ({docs.fallos?.postop || docs.fallos?.recipes}). Vuelve a desplegarlo.
                </p>
              )}
              {/* Consentimientos */}
              <div>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1">
                  <FilePen className="w-3 h-3 text-[#2E6E5E]" /> Consentimientos{falloConsents ? '' : ` (${docs.consents.length})`}
                </div>
                {falloConsents ? (
                  /* Ni el número, ni «adjuntar», ni «generar»: con la lista sin
                     leer, generar duplica y adjuntar no sabe qué hay ya. */
                  <p className="text-sm text-red-700 py-1" data-testid={`parte-consents-fallo-${item.patient_id}`}>
                    No se han podido cargar los consentimientos de este paciente ({falloConsents}).
                    No se puede decir si tiene el CI firmado: vuelve a desplegarlo antes de pasarlo a quirófano.
                  </p>
                ) : (<>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs mb-2 border-[#2E6E5E]/50"
                  onClick={() => setAdjuntarOpen((v) => !v)}
                  data-testid={'parte-consent-adjuntar-' + item.patient_id}
                >
                  <FilePen className="w-3 h-3 mr-1.5" />
                  {adjuntarOpen ? 'Cerrar lista de plantillas' : 'Adjuntar consentimientos…'}
                </Button>
                {adjuntarOpen && (
                  <div className="mb-2 max-w-md rounded-md border border-border bg-white">
                    <div className="max-h-52 overflow-y-auto divide-y divide-border">
                      {/* `null` = no se han podido leer (22/9/2026): antes se
                          quedaba en «Cargando plantillas…» para siempre. */}
                      {consentTemplates === null && (
                        <div className="px-3 py-2 text-xs text-red-700">
                          No se han podido leer las plantillas de consentimiento. Recarga la página para volver a pedirlas.
                        </div>
                      )}
                      {consentTemplates?.length === 0 && (
                        <div className="px-3 py-2 text-xs text-muted-foreground">Cargando plantillas…</div>
                      )}
                      {(consentTemplates || []).map((template) => {
                        const alreadyAttached = docs.consents.some((consent) => consent.template_code === template.code);
                        return (
                          <label
                            key={template.code}
                            className={`flex items-center gap-2 px-3 py-1.5 text-xs ${alreadyAttached ? 'text-muted-foreground' : 'cursor-pointer hover:bg-[rgba(46,110,94,0.05)]'}`}
                            data-testid={`parte-template-${template.code}-${item.patient_id}`}
                          >
                            <input
                              type="checkbox"
                              className="w-3.5 h-3.5 accent-[#2E6E5E] shrink-0"
                              disabled={alreadyAttached}
                              checked={selTemplates.has(template.code)}
                              onChange={() => alternarTemplate(template.code)}
                            />
                            <span>{alreadyAttached ? '✓ ' : ''}{template.name}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div className="border-t border-border px-2 py-1.5">
                      <Button
                        size="sm"
                        className="h-7 text-xs bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                        onClick={adjuntarSeleccion}
                        disabled={addingConsent || selTemplates.size === 0}
                        data-testid={`parte-adjuntar-lote-${item.patient_id}`}
                      >
                        {addingConsent
                          ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                          : <FilePen className="w-3 h-3 mr-1.5" />}
                        Adjuntar {selTemplates.size > 1 ? `${selTemplates.size} consentimientos` : 'seleccionado'}
                      </Button>
                    </div>
                  </div>
                )}
                {!docs.consents.length && item.cita?.id && !consultaQx && (
                  <Button
                    size="sm"
                    className="h-7 text-xs bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                    onClick={generarConsentimientos}
                    disabled={ensuringConsents}
                    data-testid={`parte-consent-generar-${item.patient_id}`}
                    title="Generar el consentimiento del procedimiento y prepararlo para la tablet"
                  >
                    {ensuringConsents
                      ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                      : <FilePen className="w-3 h-3 mr-1.5" />}
                    Generar consentimiento
                  </Button>
                )}
                {(unsignedConsents.length > 1 || selConsents.size > 0) && (
                  <div className="inline-flex items-center gap-1.5 mr-1">
                    {selConsents.size > 0 && (
                      <Button
                        size="sm"
                        className="h-7 text-xs bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                        onClick={() => enviarLoteTablet(Array.from(selConsents))}
                        disabled={!!sendingConsentId}
                        data-testid={`parte-consents-tablet-seleccion-${item.patient_id}`}
                        title="Enviar los consentimientos marcados a la tablet"
                      >
                        {sendingConsentId
                          ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                          : <Tablet className="w-3 h-3 mr-1.5" />}
                        Enviar a tablet ({selConsents.size})
                      </Button>
                    )}
                    {unsignedConsents.length > 1 && selConsents.size === 0 && (
                      <Button
                        size="sm"
                        className="h-7 text-xs bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                        onClick={enviarTodosPendientesTablet}
                        disabled={!!sendingConsentId}
                        data-testid={`parte-consents-tablet-todos-${item.patient_id}`}
                        title="Enviar todos los consentimientos no firmados de este paciente a la tablet"
                      >
                        {sendingConsentId
                          ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                          : <Tablet className="w-3 h-3 mr-1.5" />}
                        Enviar pendientes a tablet
                      </Button>
                    )}
                  </div>
                )}
                {docs.consents.map((c) => (
                  <div key={c.id} className="inline-flex items-center gap-1">
                    {!c.patient_signature_b64 && !noSePide(c) && (
                      <input
                        type="checkbox"
                        className="w-3.5 h-3.5 accent-[#2E6E5E] cursor-pointer"
                        checked={selConsents.has(c.id)}
                        onChange={() => alternarConsent(c.id)}
                        title="Marcar para enviarlos juntos a la tablet"
                        data-testid={`parte-consent-check-${c.id}`}
                      />
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => setPreview(consentPreviewArgs({
                        consentId: c.id, templateCode: c.template_code, templateName: c.template_name, patientName,
                      }))}
                      data-testid={`parte-consent-pdf-${c.id}`}
                      title="Ver consentimiento en PDF"
                    >
                      <Eye className="w-3 h-3 mr-1.5" /> {truncate(c.template_name, 22)}
                    </Button>
                    {c.patient_signature_b64 ? (
                      <span
                        className="inline-flex h-7 items-center rounded border border-emerald-200 bg-emerald-50 px-2 text-xs font-medium text-emerald-700"
                        title="Consentimiento firmado por el paciente"
                        data-testid={`parte-consent-firmado-${c.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Firmado
                      </span>
                    ) : noSePide(c) ? (
                      /* D3 (23/9/2026): no se pide. Si hay sedación, el
                         anestesista lo adjunta desde la hoja de anestesia.
                         Sin sedación en el registro (8/10/2026), se sale
                         cambiándolo allí. */
                      <span
                        className="inline-flex h-7 items-center rounded border border-border px-2 text-xs text-muted-foreground"
                        title={sinSedacionDelDia
                          ? 'Marcada sin sedación en el registro quirúrgico: no se le pide a la paciente. Si hubo sedación, cámbialo en el registro.'
                          : 'Esta intervención no lleva anestesia: no se le pide a la paciente. Si hay sedación, el anestesista lo adjunta desde la hoja de anestesia.'}
                        data-testid={`parte-consent-no-lleva-${c.id}`}
                      >
                        No se pide
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant={queuedConsentIds.has(c.id) ? 'default' : 'outline'}
                        className={`h-7 text-xs ${queuedConsentIds.has(c.id)
                          ? 'bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white'
                          : 'border-[#2E6E5E] text-[#13231E]'}`}
                        onClick={() => enviarConsentimientoTablet(c)}
                        disabled={!!sendingConsentId}
                        data-testid={`parte-consent-tablet-${c.id}`}
                        title="Enviar este consentimiento a la tablet de firma"
                      >
                        {sendingConsentId === c.id
                          ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                          : <Tablet className="w-3 h-3 mr-1.5" />}
                        {queuedConsentIds.has(c.id) ? 'En tablet' : 'Tablet'}
                      </Button>
                    )}
                  </div>
                ))}
                </>)}
              </div>

              {/* Indicaciones postoperatorias */}
              <DocGroup icon={BedDouble} label="Indicaciones postoperatorias" count={docs.postop.length}>
                {docs.postop.map((p) => (
                  <Button
                    key={p.id}
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => setPreview(postopPreviewArgs({
                      docId: p.id, templateName: p.template_name, patientName,
                    }))}
                    data-testid={`parte-postop-${p.id}`}
                  >
                    <Printer className="w-3 h-3 mr-1.5" /> {truncate(p.template_name || p.surgery_name || 'Postoperatorio', 22)}
                    {p.surgery_date && <span className="ml-1 text-muted-foreground tabular-nums">· {formatDate(p.surgery_date)}</span>}
                  </Button>
                ))}
              </DocGroup>

              {/* Recetas */}
              <DocGroup icon={Pill} label="Recetas" count={docs.recipes.length}>
                {selRecipes.size > 0 && (
                  <Button
                    size="sm"
                    className="h-7 text-xs bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                    onClick={() => {
                      const ids = docs.recipes.filter((r) => selRecipes.has(r.id)).map((r) => r.id);
                      setPreview({
                        open: true,
                        url: `${BACKEND_URL}/api/recetas/lote/pdf?ids=${ids.join(',')}`,
                        filename: `recetas_${(patientName || 'paciente').replace(/\s+/g, '_')}.pdf`,
                        title: `${ids.length} recetas · ${patientName}`,
                      });
                      setSelRecipes(new Set());
                      setTimeout(cargar, 1200);
                    }}
                    title="Las que estén en borrador se emiten (consumen su CVE) y salen todas en un único PDF"
                    data-testid={`parte-recetas-lote-${item.patient_id}`}
                  >
                    <Printer className="w-3 h-3 mr-1.5" /> Imprimir seleccionadas ({selRecipes.size})
                  </Button>
                )}
                {docs.recipes.map((r) => (
                  <div key={r.id} className="inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5 accent-[#2E6E5E] cursor-pointer"
                      checked={selRecipes.has(r.id)}
                      onChange={() => alternarRecipe(r.id)}
                      title="Marcar para imprimir en lote"
                      data-testid={`parte-receta-check-${r.id}`}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => setPreview(recipePreviewArgs({
                        recipeId: r.id, medicationName: r.medication_name, patientName,
                      }))}
                      data-testid={`parte-receta-${r.id}`}
                    >
                      <Printer className="w-3 h-3 mr-1.5" /> {truncate(r.medication_name, 22)}
                    </Button>
                  </div>
                ))}
              </DocGroup>

              {docs.postop.length > 0 && (
                <PostopRecipesBundle
                  postop={docs.postop}
                  recipes={docs.recipes}
                  patientName={patientName}
                  setPreview={setPreview}
                />
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/** Bloque de un tipo de documento; se oculta si no hay ninguno. */
function DocGroup({ icon: Icon, label, count, children }) {
  if (!count) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1">
        <Icon className="w-3 h-3 text-[#2E6E5E]" /> {label} ({count})
      </div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

/** Cabecera + lista de pacientes de una sesión (un quirófano del día). */
function SesionParte({ sesion, meds, consentTemplates, conceptos, setPreview, onCitaChanged }) {
  const items = useMemo(
    () => [...(sesion.items || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [sesion.items],
  );
  return (
    <div className="cjo-card p-4 sm:p-5" data-testid={`parte-sesion-${sesion.id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="font-semibold text-[#13231E]">{sesion.doctor_name}</span>
        {sesion.cirujano && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Stethoscope className="w-3 h-3 text-[#2E6E5E]" /> Cirujano: {sesion.cirujano}
          </span>
        )}
        {sesion.clinica && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Stethoscope className="w-3 h-3 text-[#2E6E5E]" /> {sesion.clinica}
          </span>
        )}
        {sesion.lugar && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="w-3 h-3 text-[#2E6E5E]" /> {sesion.lugar}
          </span>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Sin pacientes asignados en este quirófano.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((it, i) => (
            <PacienteParte
              key={it.id || i}
              index={i}
              item={it}
              meds={meds}
              surgeryDate={sesion.date}
              consentTemplates={consentTemplates}
              conceptos={conceptos}
              setPreview={setPreview}
              esExterna={!!sesion.is_externa}
              onCitaChanged={onCitaChanged}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * @param sesiones     sesiones del planificador antiguo; `null` = NO SE HAN
 *                     PODIDO LEER (no es lo mismo que ninguna).
 * @param agendas      todas las agendas (aquí se filtran las de quirófano);
 *                     `null` = no se han podido leer.
 * @param fallosCarga  { agendas?: motivo, sesiones?: motivo } cuando algo de
 *                     lo anterior no se ha leído.
 * @param onReintentar vuelve a pedir agendas y sesiones (lo hace la página).
 */
export default function ParteDelDia({ sesiones, agendas, fallosCarga, onReintentar, setPreview }) {
  // Catálogo de medicamentos favoritos (para las recetas de un clic).
  // `null` en cualquiera de los dos = NO SE HA PODIDO LEER, que no es lo
  // mismo que vacío (22/9/2026): un error o un 200 que no es una lista (el
  // index.html del SPA) se convertía en `[]`, y las plantillas se quedaban
  // en «Cargando…» para siempre y la receta tomaba la pauta del kit sin que
  // nadie lo supiera.
  const [meds, setMeds] = useState([]);
  const [consentTemplates, setConsentTemplates] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  useEffect(() => {
    api.get('/api/recetas/medications')
      .then((r) => setMeds(Array.isArray(r.data) ? r.data : null))
      .catch(() => setMeds(null));
    // Solo las activas (20/10/2026): ni las desactivadas ni los borradores
    // importados del Drive sin activar.
    api.get('/api/consent-templates')
      .then((r) => setConsentTemplates(Array.isArray(r.data)
        ? r.data.filter((t) => t.active !== false) : null))
      .catch(() => setConsentTemplates(null));
    // Los tipos de cita, con los retirados (las citas ya dadas los siguen
    // llevando), para la chapa del RETOQUE (`rotuloTipoQx`). Si no se pueden
    // leer, la chapa del retoque no sale y el parte enseña el procedimiento
    // como antes; la del chalazión la sigue diciendo el servidor en la cita.
    listConceptos({ todos: true })
      .then((r) => setConceptos(Array.isArray(r) ? r : []))
      .catch(() => setConceptos([]));
  }, []);

  // Fecha del parte: por defecto HOY; se cambia libremente.
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  // Se incrementa al cambiar la hora o duración de una cirugía, para releer el
  // parte y que salga ya reordenado.
  const [recarga, setRecarga] = useState(0);

  // La clínica agenda las cirugías como CITAS en las agendas de quirófano
  // (Quirófano Blefaroplastia, etc.), no como sesiones del planificador.
  // El parte se alimenta de esas citas; si además hay sesiones creadas a
  // mano para ese día, se muestran también.
  const qxAgendas = useMemo(
    () => (agendas || []).filter((a) => (a.tipo || '') === 'quirofano'),
    [agendas],
  );

  const [citasGroups, setCitasGroups] = useState(null); // null = cargando
  // Quirófanos que NO se han podido leer ese día: [{ agenda, motivo }].
  const [fallosLectura, setFallosLectura] = useState([]);
  useEffect(() => {
    if (!date || qxAgendas.length === 0) { setCitasGroups([]); setFallosLectura([]); return undefined; }
    let cancelled = false;
    setCitasGroups(null);
    // VACÍO Y «NO SE HA PODIDO LEER» NO SON LO MISMO, TAMPOCO AQUÍ (22/9/2026,
    // G8 del informe). Cada quirófano se pedía con un `.catch(() => citas:
    // [])` y después se tiraban los grupos vacíos: un 500 del servidor se
    // enseñaba como «No hay quirófano programado para esa fecha», sin un solo
    // aviso, y enfermería podía creer que ese día no se operaba. Es la misma
    // avería que se arregló el 21/9 en los documentos del paciente (más
    // abajo, `pedirBloque`), en la puerta de al lado: el fallo se guarda con
    // su agenda (`citas: null`), no se filtra, y se pinta en rojo con un
    // botón para volver a pedirlo.
    Promise.all(qxAgendas.map((a) =>
      listCitas({ agendaId: a.id, date })
        // Un 200 que NO es una lista tampoco es «no hay nada» (22/9/2026): el
        // fallback del SPA devuelve su index.html con un 200 cuando /api/citas
        // no llega al backend, y convertirlo en `[]` volvía a enseñar «No hay
        // quirófano programado». Se trata como un fallo de lectura, con la
        // misma frase que `leerLista` en QuirofanoSesionesPage.
        .then((cs) => (Array.isArray(cs)
          ? { agenda: a, citas: cs }
          : { agenda: a, citas: null, fallo: 'el servidor ha contestado algo que no es una lista' }))
        // El motivo se pinta como texto: con `mensajeError`, que convierte
        // en frase la lista de objetos de un 422 en vez de tirar el parte.
        .catch((e) => ({ agenda: a, citas: null, fallo: mensajeError(e, 'no se ha podido consultar') })),
    )).then((res) => {
      if (cancelled) return;
      const rotos = res.filter((r) => r.citas === null);
      setFallosLectura(rotos.map((r) => ({ agenda: r.agenda, motivo: r.fallo })));
      if (rotos.length) {
        toast.error(`No se ha podido leer ${rotos.map((r) => r.agenda.name).join(', ')}. `
          + 'Lo que ves no es lo que hay programado.');
      }
      setCitasGroups(res
        .filter((r) => Array.isArray(r.citas) && r.citas.length > 0)
        .map(({ agenda, citas }) => {
          const orden = [...citas].sort((x, y) => (x.start || '').localeCompare(y.start || ''));
          return {
            id: `ag-${agenda.id}`,
            date,
            doctor_name: agenda.name,
            cirujano: orden.find((c) => c.cirujano)?.cirujano || '',
            lugar: orden.find((c) => c.lugar)?.lugar || '',
            clinica: '',
            is_externa: !!agenda.is_externa,
            items: orden.map((c, i) => ({
              id: c.id,
              order: i,
              patient_id: c.patient_id,
              patient_name: c.patient_name,
              // El tipo/procedimiento corto (para el título de la fila).
              procedure_text: c.procedimiento || c.concept_name || '',
              // La NOTA de la cita: procedimiento detallado y alergias que
              // escribe el equipo. Se muestra íntegra bajo el paciente.
              notes: c.notes || '',
              // Las alergias de la FICHA, resueltas por el servidor con la
              // misma regla que la hoja de anestesia y el PDF («gana el sí»,
              // `services/datos_permitidos`). Hasta el 22/9/2026 aquí solo
              // se veía la nota de la cita (G9 del informe).
              alergias: c.alergias,
              alergias_estado: c.alergias_estado,
              time: c.start,
              // La agenda de quirófano a la que pertenece la cita. Sin esto la
              // hoja de anestesia recibía agendaId=undefined.
              agenda_id: agenda.id,
              // La cita completa, para poder cambiarle la hora o la duración
              // sin salir del parte del día.
              cita: c,
              // Tiempos reales de cirugía (quirófano de externos → facturar).
              cirugia_inicio: c.cirugia_inicio || '',
              cirugia_fin: c.cirugia_fin || '',
              cirugia_duracion_min: c.cirugia_duracion_min,
            })),
          };
        }));
    });
    return () => { cancelled = true; };
  }, [date, qxAgendas, recarga]);

  // Sesiones del planificador de ese día (si las hay) + quirófanos por citas.
  const delDia = useMemo(() => {
    const ses = (sesiones || []).filter((s) => s.date === date)
      .sort((a, b) => (a.shift !== b.shift ? (a.shift === 'manana' ? -1 : 1) : (a.doctor_name || '').localeCompare(b.doctor_name || '')));
    return [...ses, ...(citasGroups || [])];
  }, [sesiones, date, citasGroups]);

  const cargando = citasGroups === null;

  // LA PUERTA DE AL LADO DE G8 (segunda pasada, 22/9/2026): un nivel por
  // encima de las citas, la LISTA de quirófanos y las sesiones del
  // planificador también pueden no haberse leído. La página lo dice pasando
  // `null` (no leídas) en vez de `[]` (ninguna): con cero quirófanos «leídos»
  // esto pintaba «No hay quirófano programado para esa fecha» con la base de
  // datos caída. Se pinta en el mismo bloque rojo, y «no hay quirófano» solo
  // vale cuando TODO ha contestado.
  const motivoDe = (m) => (typeof m === 'string' && m.trim() ? m.trim() : 'no se ha podido consultar');
  const agendasNoLeidas = agendas === null;
  const sesionesNoLeidas = sesiones === null;
  const algoSinLeer = agendasNoLeidas || sesionesNoLeidas || fallosLectura.length > 0;
  const reintentar = () => {
    setRecarga((n) => n + 1);
    if (agendasNoLeidas || sesionesNoLeidas) onReintentar?.();
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">Fecha del quirófano</label>
        <div className="flex items-center gap-1.5">
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-44"
            data-testid="parte-fecha"
          />
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => setDate((d) => addDaysStr(d || new Date().toISOString().slice(0, 10), -1))}
            aria-label="Día anterior"
            data-testid="parte-dia-anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            className="h-9"
            onClick={() => setDate(new Date().toISOString().slice(0, 10))}
            data-testid="parte-hoy"
          >
            Hoy
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => setDate((d) => addDaysStr(d || new Date().toISOString().slice(0, 10), 1))}
            aria-label="Día siguiente"
            data-testid="parte-dia-siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Un quirófano que no se ha podido leer se dice, y se dice ANTES del
          resto: «no hay quirófano» solo vale cuando todos han contestado. */}
      {!cargando && algoSinLeer && (
        <div
          className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 space-y-2"
          role="alert"
          data-testid="parte-fallo-lectura"
        >
          {agendasNoLeidas && (
            <p className="text-sm text-red-800" data-testid="parte-fallo-agendas">
              <span className="font-semibold">No se han podido leer los quirófanos:</span>
              {' '}{motivoDe(fallosCarga?.agendas)}. Lo que ves debajo no es lo que hay programado.
            </p>
          )}
          {sesionesNoLeidas && (
            <p className="text-sm text-red-800" data-testid="parte-fallo-sesiones">
              <span className="font-semibold">No se han podido leer las sesiones del planificador:</span>
              {' '}{motivoDe(fallosCarga?.sesiones)}. Lo que ves debajo no es lo que hay programado.
            </p>
          )}
          {fallosLectura.map((f) => (
            <p key={f.agenda.id} className="text-sm text-red-800">
              <span className="font-semibold">No se ha podido leer el quirófano {f.agenda.name}:</span>
              {' '}{motivoDe(f.motivo)}. Lo que ves debajo no es lo que hay programado.
            </p>
          ))}
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs border-red-400 text-red-800 hover:bg-red-100"
            onClick={reintentar}
            data-testid="parte-fallo-reintentar"
          >
            Reintentar
          </Button>
        </div>
      )}
      {cargando ? (
        <div className="cjo-card p-8 text-center text-sm text-muted-foreground">Cargando quirófanos…</div>
      ) : delDia.length === 0 && !algoSinLeer ? (
        <div className="cjo-card p-8 text-center" data-testid="parte-vacio">
          <CalendarRange className="w-8 h-8 text-[#2E6E5E] mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            No hay quirófano programado para esa fecha.
          </p>
        </div>
      ) : (
        delDia.map((s) => (
          <SesionParte key={s.id} sesion={s} meds={meds} consentTemplates={consentTemplates} conceptos={conceptos} setPreview={setPreview} onCitaChanged={() => setRecarga((n) => n + 1)} />
        ))
      )}
    </div>
  );
}
